// MemberSelector — pick the ordered list of member repos that back a
// virtual repo. Used by Repos.tsx (create form) and RepoDetail.tsx
// (edit form) so the constraint logic and the ordering UI live in one
// place.
//
// Backend constraints (api/internal/handlers/repos.go.validateMembers)
// the selector has to mirror:
//   - members live in the same project as the parent virtual
//   - members carry the same format as the parent
//   - members are not themselves virtuals (no nested virtuals)
//   - members ≠ the parent repo (no self-reference)
//   - no duplicates
//
// The "available" column lists the repos that pass the first three
// filters; the "selected" column is the ordered slice that ends up on
// the wire as `members: string[]`.

import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';
import { useRepos, type Repo } from '../api/client';

interface Props {
  readonly projectId: string;
  readonly format: Repo['format'] | '';
  /** repo id to exclude from the candidate list (the parent repo on edit). */
  readonly excludeId?: string;
  /** ordered list of currently-selected member ids. */
  readonly value: string[];
  readonly onChange: (next: string[]) => void;
}

export function MemberSelector({ projectId, format, excludeId, value, onChange }: Props) {
  const { data: allRepos } = useRepos();

  if (!projectId || !format) {
    return (
      <p className="text-xs text-fg-muted italic">
        Pick a project and a format first — members must match both.
      </p>
    );
  }

  const candidates: Repo[] = (allRepos ?? []).filter(
    (r) =>
      r.projectId === projectId &&
      r.format === format &&
      r.kind !== 'virtual' &&
      r.id !== excludeId &&
      !value.includes(r.id),
  );

  // The selected list is rendered in the same order as `value`. We map
  // ids back to their repo objects (or fall back to a placeholder when
  // a member id no longer exists, which can happen if a member was
  // deleted between load and edit).
  const selected = value.map((id) => allRepos?.find((r) => r.id === id) ?? { id, slug: '⟨deleted⟩', name: '', kind: 'local', format, projectId });

  const add = (id: string) => onChange([...value, id]);
  const remove = (id: string) => onChange(value.filter((m) => m !== id));
  const moveUp = (i: number) => {
    if (i === 0) return;
    const next = [...value];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    onChange(next);
  };
  const moveDown = (i: number) => {
    if (i >= value.length - 1) return;
    const next = [...value];
    [next[i], next[i + 1]] = [next[i + 1], next[i]];
    onChange(next);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {/* Selected — ordered list. Order is the resolution priority. */}
      <div className="rounded-md border border-border bg-bg-inset/40 p-3 min-h-[8rem]">
        <p className="orb-section-title text-fg-muted mb-2">
          Members in resolution order ({value.length})
        </p>
        {value.length === 0 ? (
          <p className="text-xs text-fg-muted italic">
            No members yet. Add repos from the right; the first row is consulted first on pull.
          </p>
        ) : (
          <ol className="space-y-1.5">
            {selected.map((r, i) => (
              <li
                key={r.id}
                className="flex items-center gap-2 px-2 py-1.5 rounded bg-bg-elevated border border-border"
              >
                <span className="text-xs text-fg-subtle tabular-nums w-5">{i + 1}.</span>
                <span className="font-mono text-xs text-accent flex-1 truncate" title={r.slug}>
                  {r.slug}
                </span>
                <span className="text-xs text-fg-muted truncate max-w-[8rem]" title={r.name}>
                  {r.name}
                </span>
                <button
                  type="button"
                  onClick={() => moveUp(i)}
                  disabled={i === 0}
                  className="p-1 text-fg-muted hover:text-fg disabled:opacity-30"
                  aria-label="Move up"
                >
                  <ArrowUp className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => moveDown(i)}
                  disabled={i === value.length - 1}
                  className="p-1 text-fg-muted hover:text-fg disabled:opacity-30"
                  aria-label="Move down"
                >
                  <ArrowDown className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => remove(r.id)}
                  className="p-1 text-fg-muted hover:text-danger"
                  aria-label="Remove"
                >
                  <X className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* Available — pool of repos that match project + format and
          aren't already selected. Clicking + adds them to the bottom
          of the selected list. */}
      <div className="rounded-md border border-border bg-bg-inset/40 p-3 min-h-[8rem]">
        <p className="orb-section-title text-fg-muted mb-2">
          Available in this project ({candidates.length})
        </p>
        {candidates.length === 0 ? (
          <p className="text-xs text-fg-muted italic">
            {(allRepos ?? []).length === 0
              ? 'Loading repositories…'
              : 'No matching repos. Members must share the same project and format and cannot themselves be virtual.'}
          </p>
        ) : (
          <ul className="space-y-1.5 max-h-64 overflow-auto">
            {candidates.map((r) => (
              <li
                key={r.id}
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-bg-hover/60"
              >
                <span className="font-mono text-xs text-accent flex-1 truncate" title={r.slug}>
                  {r.slug}
                </span>
                <span className="text-xs text-fg-muted truncate max-w-[8rem]" title={r.name}>
                  {r.name}
                </span>
                <span className="text-[10px] text-fg-subtle uppercase tracking-wide">
                  {r.kind}
                </span>
                <button
                  type="button"
                  onClick={() => add(r.id)}
                  className="p-1 text-accent hover:text-fg"
                  aria-label="Add member"
                >
                  <Plus className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
